import { Button, useDisclosure, useToast } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ComplianceDocumentDefinition,
  ComplianceDocumentDefinitionSearchQuery,
  complianceDocumentDefinitionsApi,
} from '@texas/api/endpoints/metadata/complianceDocumentDefinitionsApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { SearchFilterInput } from '@texas/components/SearchFilterInput';
import { ConfigurationPage } from '@texas/components/configuration/layout/ConfigurationPage';
import { DataTableContainer } from '@texas/components/shared/dataTable/DataTableContainer';
import { TexasSwitch } from '@texas/components/shared/form/TexasSwitch';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { ServerError } from '@texas/types';
import { request } from '@texas/utils/helpers/httpHelpers';
import { ArchiveComplianceDocumentDefinition } from './ArchiveComplianceDocumentDefinition';
import { complianceDocumentDefinitionTableColumns } from './Columns';
import { CreateComplianceDocumentDefinition } from './CreateComplianceDocumentDefinition';
import { UpdateComplianceDocumentDefinition } from './UpdateComplianceDocumentDefinition';

export function ComplianceDocumentDefinitions({
  folderId,
}: {
  folderId: number;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [searchQuery, setSearchQuery] =
    useState<ComplianceDocumentDefinitionSearchQuery>({
      searchTerm: '',
      includeArchived: false,
      sortBy: 'name',
      sortDesc: false,
    });
  const {
    isOpen: isCreateOpen,
    onOpen: onCreateOpen,
    onClose: onCreateClose,
  } = useDisclosure();
  const {
    value: selectedDefinition,
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useValueDisclosure<ComplianceDocumentDefinition>();
  const {
    value: archiveDefinition,
    isOpen: isArchiveOpen,
    onOpen: onArchiveOpen,
    onClose: onArchiveClose,
  } = useValueDisclosure<ComplianceDocumentDefinition>();
  const {
    data: definitions,
    refetch,
    loading,
    error,
  } = useApiResource(complianceDocumentDefinitionsApi.getAll);
  const { request: restore } = useApiRequest(
    complianceDocumentDefinitionsApi.restore,
  );

  useEffect(() => {
    refetch(searchQuery);
  }, [refetch, searchQuery]);

  const restoreDefinition = useCallback(
    async (id: number) => {
      await request(
        restore,
        [id],
        () => {
          toast({
            title: t('general.restored'),
            status: 'success',
            isClosable: true,
          });
          refetch(searchQuery);
        },
        (restoreError: ServerError) => {
          toast({
            title: t('general.restoreFailed'),
            description: restoreError.message,
            status: 'error',
            isClosable: true,
          });
        },
      );
    },
    [refetch, restore, searchQuery, t, toast],
  );

  const columns = useMemo(
    () =>
      complianceDocumentDefinitionTableColumns(
        t,
        onEditOpen,
        onArchiveOpen,
        restoreDefinition,
      ),
    [onArchiveOpen, onEditOpen, restoreDefinition, t],
  );

  const reload = () => refetch(searchQuery);

  return (
    <ConfigurationPage
      title={t('complianceDocumentDefinitions.title')}
      description={t('complianceDocumentDefinitions.description')}
      action={
        <Button variant="entityDirectoryPrimary" onClick={onCreateOpen}>
          {t('complianceDocumentDefinitions.newDefinition')}
        </Button>
      }
      toolbar={
        <>
          <SearchFilterInput
            value={searchQuery.searchTerm}
            style="entityDirectory"
            onChange={(searchTerm) =>
              setSearchQuery({ ...searchQuery, searchTerm })
            }
          />
          <TexasSwitch
            checked={!searchQuery.includeArchived}
            label={t('complianceDocumentDefinitions.hideArchived')}
            onChange={(hideArchived) =>
              setSearchQuery({
                ...searchQuery,
                includeArchived: !hideArchived,
              })
            }
          />
        </>
      }
    >
      <UpdateComplianceDocumentDefinition
        definition={selectedDefinition ?? null}
        folderId={folderId}
        isOpen={isEditOpen}
        onClose={onEditClose}
        onUpdated={reload}
      />
      <CreateComplianceDocumentDefinition
        folderId={folderId}
        isOpen={isCreateOpen}
        onClose={onCreateClose}
        onCreated={reload}
      />
      {archiveDefinition && (
        <ArchiveComplianceDocumentDefinition
          definition={archiveDefinition}
          isOpen={isArchiveOpen}
          onClose={onArchiveClose}
          onArchived={reload}
        />
      )}
      <DataTableContainer
        w="100%"
        showColumnToggle={false}
        datatable={{
          data: definitions ?? [],
          sorted: {
            key: searchQuery.sortBy,
            desc: searchQuery.sortDesc,
            onSortedChange: ({ key, desc }) =>
              setSearchQuery({ ...searchQuery, sortBy: key, sortDesc: desc }),
          },
          isLoading: loading,
          columns,
          variant: 'entity-directory',
        }}
        error={error}
      />
    </ConfigurationPage>
  );
}
